/**
 * Visual indicator for offline status and pending sync queue.
 *
 * | State                     | Display                                        |
 * |---------------------------|------------------------------------------------|
 * | Online, no pending        | Hidden                                         |
 * | Online, syncing           | Blue bar: "Syncing N changes..."               |
 * | Online, pending > 0       | Amber bar: "N changes pending"                 |
 * | Offline, no pending       | Gray bar: "You're offline"                     |
 * | Offline, pending > 0      | Gray bar: "Offline — N changes will sync..."   |
 *
 * @module components/OfflineIndicator
 */

import { useOnlineStatus } from '../hooks/useOnlineStatus';

export function OfflineIndicator() {
  const { isOnline, pendingCount, isSyncing } = useOnlineStatus();

  // Online with nothing pending — don't render
  if (isOnline && pendingCount === 0 && !isSyncing) {
    return null;
  }

  let bgColor: string;
  let message: string;

  if (!isOnline) {
    bgColor = 'bg-gray-600';
    message =
      pendingCount > 0
        ? `Offline — ${pendingCount} change${pendingCount === 1 ? '' : 's'} will sync when back online`
        : "You're offline";
  } else if (isSyncing) {
    bgColor = 'bg-blue-600';
    message = `Syncing ${pendingCount} change${pendingCount === 1 ? '' : 's'}...`;
  } else {
    // Online with pending items (not currently syncing)
    bgColor = 'bg-amber-600';
    message = `${pendingCount} change${pendingCount === 1 ? '' : 's'} pending`;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={`${bgColor} px-4 py-1.5 text-center text-xs font-medium text-white`}
    >
      {isSyncing && (
        <span
          className="mr-2 inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent"
          aria-hidden="true"
        />
      )}
      {message}
    </div>
  );
}
